import { useMyBubblesQuery } from "@crowdcoursing/api/graphql";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { useRoomDashboard } from "~hooks/useRoomDashboard";
import { useRoomSlideouts } from "~hooks/useRoomSlideouts";
import { useSelector } from "~hooks/useTypedRedux";
import { ConfirmDeleteDialog } from "~ui/molecules/ConfirmDeleteDialog/ConfirmDeleteDialog";
import { RoomDashboardTemplate } from "~ui/templates/RoomDashboardTemplate/RoomDashboardTemplate";

export const RoomDashboardPage = () => {
  const { roomId = "" } = useParams();
  const userId = useSelector((state) => state.user?.id) || "";
  const { data: myBubbles } = useMyBubblesQuery();

  const {
    room,
    isLoading,
    error,
    imageUrl,
    status,
    uploadImage,
    memberData,
    requestsData,
    chatMembers,
    resourceManagement,
    taskManagement,
    milestoneManagement,
    handleJoinRoom,
    handleLeaveRoom,
    handleCancelRequest,
    handleShareRoom,
    handleAddMember,
    handleRemoveMember,
    handleApproveMember,
    handleRejectMember,
    handleAddFacilitator,
    handleRemoveFacilitator,
    handleAddOwner,
    handleAddEvent,
    pendingRemovalMember,
    confirmRemoveMember,
    handleDeleteRoom,
    handleUpdateRoom,
  } = useRoomDashboard({
    roomId,
    userId,
  });

  const slideouts = useRoomSlideouts();
  const [isDeleteRoomDialogOpen, setIsDeleteRoomDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: "100vh", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !room) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Error loading room</Typography>
      </Box>
    );
  }

  return (
    <>
      <ConfirmDeleteDialog
        open={Boolean(pendingRemovalMember)}
        onClose={() => confirmRemoveMember(false)}
        onConfirm={() => confirmRemoveMember(true)}
        itemType="member"
        itemName={pendingRemovalMember?.name || ""}
        itemDescription={`from ${room.name}`}
      />
      <ConfirmDeleteDialog
        open={isDeleteRoomDialogOpen}
        onClose={() => setIsDeleteRoomDialogOpen(false)}
        onConfirm={handleDeleteRoom}
        itemType="room"
        itemName={room.name}
        itemDescription={room.description}
      />
      <RoomDashboardTemplate
        room={room}
        imageUrl={imageUrl}
        status={status}
        onImageChange={uploadImage}
        onJoinRoom={handleJoinRoom}
        onLeaveRoom={handleLeaveRoom}
        onCancelRequest={handleCancelRequest}
        onShareRoom={handleShareRoom}
        onAddMember={handleAddMember}
        members={memberData?.members ?? []}
        requests={requestsData?.requests ?? []}
        onRemoveMember={handleRemoveMember}
        onApproveMember={handleApproveMember}
        onRejectMember={handleRejectMember}
        // Facilitator management
        onAddFacilitator={handleAddFacilitator}
        onRemoveFacilitator={handleRemoveFacilitator}
        onAddOwner={handleAddOwner}
        // Resource management
        onAddResource={resourceManagement.saveResource}
        onEditResource={resourceManagement.saveResource}
        onDeleteResource={resourceManagement.deleteResource}
        // Task management
        onAddTask={taskManagement.createTask}
        onDeleteTask={taskManagement.deleteTask}
        onCompleteTask={taskManagement.completeTask}
        // Milestone management
        onAddMilestone={milestoneManagement.createMilestone}
        onDeleteMilestone={milestoneManagement.deleteMilestone}
        onCompleteMilestone={milestoneManagement.completeMilestone}
        // Event management
        onAddEvent={handleAddEvent}
        bubbleSelectItems={myBubbles?.me.bubbles.map(({ id, name }) => ({ id, value: name })) ?? []}
        defaultBubbleId={room.bubbleId}
        chatMemberCount={chatMembers.length}
        // Slideout state
        resourcesSlideoutOpen={slideouts.resources.isOpen}
        membersSlideoutOpen={slideouts.members.isOpen}
        chatSlideoutOpen={slideouts.chat.isOpen}
        eventsSlideoutOpen={slideouts.events.isOpen}
        onResourcesClose={slideouts.resources.close}
        onMembersClose={slideouts.members.close}
        onChatClose={slideouts.chat.close}
        onEventsClose={slideouts.events.close}
        onResourcesClick={slideouts.resources.open}
        onMembersClick={slideouts.members.open}
        onChatClick={slideouts.chat.open}
        onEventsClick={slideouts.events.open}
        onUpdateRoom={handleUpdateRoom}
      />
    </>
  );
};
